import { useState, useRef } from 'react';
import { MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';

export const useMoveGroupsAndAppsDnD = ({
    moveGroups,
    applications,
    handleDragAssignVMs,
    handleDragReassignApplication,
    onDragToCreateGroup,
    onDragToCreateApp,
}) => {
    const [dragAssignModalOpen, setDragAssignModalOpen] = useState(false);
    const [dragAssignModalMessage, setDragAssignModalMessage] = useState('');
    const [dragAssignModalTitle, setDragAssignModalTitle] = useState('');
    const [dragAssignHandler, setDragAssignHandler] = useState(() => () => {});
    const [isDraggingVM, setIsDraggingVM] = useState(false);
    const [draggingVMRecord, setDraggingVMRecord] = useState(null);
    const vmDragTargetRef = useRef(null);

    const masterSensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    );

    const showDragAssignModal = (title, message, handler) => {
        setDragAssignModalMessage(message);
        setDragAssignHandler(() => handler);
        setDragAssignModalOpen(true);
        setDragAssignModalTitle(title);
    };

    // Build confirmation modal title/message/handler based on drag type and target
    const handleDragAssignment = (dragData) => {
        const { type, sourceId, sourceIds, targetType, targetId, sourceName, targetName } = dragData;

        let title = '';
        let message = '';
        let handler = () => {};

        if (type === 'vm') {
            title = targetType === 'application' ? 'Confirm Application Assignment' : 'Confirm Move Group Assignment';
            const vmCount = sourceIds?.length ?? 1;
            const vmLabel = vmCount === 1 ? '1 VM' : `${vmCount} VMs`;
            message = `Are you sure you want to assign ${vmLabel} to "${targetName}"?`;

            if (targetType === 'moveGroup') {
                handler = () => handleDragAssignVMs(sourceIds || [sourceId], targetId, undefined);
            } else if (targetType === 'application') {
                handler = () => {
                    // Preserve the app's move group so the VM isn't stripped from that group
                    const targetApp = applications.find((a) => a.id === targetId);
                    const appMoveGroupId = targetApp?.move_group_ids?.[0];
                    handleDragAssignVMs(sourceIds || [sourceId], appMoveGroupId, targetId);
                };
            }
        } else if (type === 'application') {
            title = 'Confirm Move Group Assignment';
            message = `Are you sure you want to assign the application "${sourceName || 'Unknown'}" to "${targetName}"?`;
            handler = () => handleDragReassignApplication(sourceId, targetId);
        }

        showDragAssignModal(title, message, handler);
    };

    // Sets isDraggingVM so the sidebar enters VM-drag highlight mode
    const handleMasterDragStart = (event) => {
        document.body.classList.add('rm-dragging');
        if (event.active.data?.current?.type === 'vm') {
            setIsDraggingVM(true);
            setDraggingVMRecord(event.active.data.current.record ?? null);
        }
    };

    // Reads vmDragTargetRef instead of dnd-kit `over` (always null across DndContext boundaries)
    const handleMasterDragEnd = (event) => {
        document.body.classList.remove('rm-dragging');
        setIsDraggingVM(false);
        setDraggingVMRecord(null);
        const { active } = event;
        const dropTarget = vmDragTargetRef.current;
        vmDragTargetRef.current = null;

        if (!dropTarget || active.data?.current?.type !== 'vm') return;

        if (dropTarget.type === 'create-move-group') {
            onDragToCreateGroup?.({
                sourceId: active.data.current.sourceId,
                sourceIds: active.data.current.sourceIds,
            });
            return;
        }
        if (dropTarget.type === 'create-application') {
            onDragToCreateApp?.({
                sourceId: active.data.current.sourceId,
                sourceIds: active.data.current.sourceIds,
            });
            return;
        }

        const record = active.data.current.record;
        const sourceIds = active.data.current.sourceIds;
        const isSingleVM = !sourceIds || sourceIds.length <= 1;

        if (dropTarget.type === 'moveGroup') {
            const targetGroup = moveGroups.find((g) => g.id === dropTarget.id);
            if (targetGroup) {
                if (
                    isSingleVM &&
                    record?.move_group_id != null &&
                    String(record.move_group_id) === String(targetGroup.id)
                )
                    return;
                handleDragAssignment({
                    type: 'vm',
                    sourceId: active.data.current.sourceId,
                    sourceIds: active.data.current.sourceIds,
                    sourceName: record?.name,
                    targetType: 'moveGroup',
                    targetId: targetGroup.id,
                    targetName: targetGroup.name,
                });
            }
        } else if (dropTarget.type === 'application') {
            const targetApp = applications.find((a) => a.id === dropTarget.id);
            if (targetApp) {
                if (
                    isSingleVM &&
                    record?.application_id != null &&
                    String(record.application_id) === String(targetApp.id)
                )
                    return;
                handleDragAssignment({
                    type: 'vm',
                    sourceId: active.data.current.sourceId,
                    sourceIds: active.data.current.sourceIds,
                    sourceName: record?.name,
                    targetType: 'application',
                    targetId: targetApp.id,
                    targetName: targetApp.name,
                });
            }
        }
    };

    return {
        dragAssignModalOpen,
        setDragAssignModalOpen,
        dragAssignModalTitle,
        dragAssignModalMessage,
        dragAssignHandler,
        isDraggingVM,
        draggingVMRecord,
        vmDragTargetRef,
        masterSensors,
        handleMasterDragStart,
        handleMasterDragEnd,
        handleDragAssignment,
    };
};
